import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/AuthContext";
import { API } from "@/config";

export const Settings = () => {
  const { changePassword } = useAuth();
  const [file, setFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => { fetchLogo(); }, []);

  const fetchLogo = async () => {
    try { const response = await axios.get(`${API}/logo`, { responseType: 'blob' }); setLogoUrl(URL.createObjectURL(response.data)); }
    catch (error) { console.log("No logo uploaded yet"); }
  };

  const handleFileChange = (e) => { if (e.target.files && e.target.files[0]) setFile(e.target.files[0]); };

  const handleUpload = async () => {
    if (!file) { toast.error("Please select a file"); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try { await axios.post(`${API}/upload/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success("Logo uploaded successfully"); setFile(null); fetchLogo(); }
    catch (error) { toast.error("Error uploading logo"); }
    finally { setUploading(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) { toast.error("New passwords don't match"); return; }
    if (passwordData.newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setChangingPassword(true);
    const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
    if (result.success) { toast.success("Password changed successfully"); setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" }); }
    else { toast.error(result.error || "Failed to change password"); }
    setChangingPassword(false);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold" data-testid="settings-title">Settings</h1>
      <Card>
        <CardHeader><CardTitle>Change Password</CardTitle><CardDescription>Update your system access password</CardDescription></CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div><Label>Current Password</Label><Input type="password" value={passwordData.currentPassword} onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })} data-testid="current-password-input" required /></div>
            <div><Label>New Password</Label><Input type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} data-testid="new-password-input" required /></div>
            <div><Label>Confirm New Password</Label><Input type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} data-testid="confirm-password-input" required /></div>
            <Button type="submit" disabled={changingPassword} data-testid="change-password-btn">{changingPassword ? "Changing..." : "Change Password"}</Button>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Company Logo</CardTitle><CardDescription>Upload your company logo to appear on invoices and agreements</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          {logoUrl && (<div className="mb-4"><Label>Current Logo:</Label><div className="mt-2"><img src={logoUrl} alt="Current Logo" className="h-24 border rounded" data-testid="current-logo" /></div></div>)}
          <div><Label>Upload New Logo</Label><Input data-testid="logo-file-input" type="file" accept="image/*" onChange={handleFileChange} className="mt-2" /></div>
          <Button onClick={handleUpload} disabled={!file || uploading} data-testid="upload-logo-btn">{uploading ? "Uploading..." : "Upload Logo"}</Button>
        </CardContent>
      </Card>
    </div>
  );
};
